import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  BackupDTabFormStructure,
  FormKey,
  FormStructure
} from '@common/components/my-account/tabs/backup-destination-tab/backup-destination-tab.model';
import { StorageType } from '@common/models';

export interface EndpointAccess {
  [FormKey.AccessKey]: boolean;
  [FormKey.BucketName]: boolean;
  [FormKey.CurrentSizeBytes]: boolean;
  [FormKey.DeleteDate]: boolean;
  [FormKey.Endpoint]: boolean;
  [FormKey.EndpointSuffix]: boolean;
  [FormKey.Name]: boolean;
  [FormKey.Region]: boolean;
  [FormKey.RootFolder]: boolean;
  [FormKey.SecretKey]: boolean;
  [FormKey.StorageType]: boolean;
}

@Injectable()
export class FormBuilderService {
  private readonly fb = inject(FormBuilder);

  private readonly defaultStorageType = StorageType.WasabiUnifiedBilling;

  buildForm(): FormGroup<FormStructure> {
    return this.fb.group({
      [FormKey.AccessKey]: [null, [Validators.required]],
      [FormKey.BucketName]: [null, [Validators.required, Validators.maxLength(50)]],
      [FormKey.CurrentSizeBytes]: [null],
      [FormKey.DeleteDate]: [null],
      [FormKey.Endpoint]: [null],
      [FormKey.EndpointSuffix]: [null],
      [FormKey.Name]: [null, [Validators.required, Validators.maxLength(50)]],
      [FormKey.Region]: [null, [Validators.required]],
      [FormKey.RootFolder]: [null, [Validators.required, Validators.maxLength(50)]],
      [FormKey.SecretKey]: [null, [Validators.required, Validators.maxLength(150)]],
      [FormKey.StorageType]: [this.defaultStorageType, [Validators.required]]
    }) as FormGroup<FormStructure>;
  }

  defaultFormValue(): BackupDTabFormStructure {
    return {
      [FormKey.AccessKey]: null,
      [FormKey.BucketName]: null,
      [FormKey.CurrentSizeBytes]: null,
      [FormKey.DeleteDate]: null,
      [FormKey.Endpoint]: null,
      [FormKey.EndpointSuffix]: null,
      [FormKey.Name]: null,
      [FormKey.Region]: null,
      [FormKey.RootFolder]: null,
      [FormKey.SecretKey]: null,
      [FormKey.StorageType]: this.defaultStorageType
    };
  }

  checkExtraControls(formGroup: FormGroup<FormStructure>, context: EndpointAccess): void {
    const {
      [FormKey.AccessKey]: isAccessKey,
      [FormKey.BucketName]: isBucketName,
      [FormKey.CurrentSizeBytes]: isCurrentSizeBytes,
      [FormKey.DeleteDate]: isDeleteDate,
      [FormKey.Endpoint]: isEndpoint,
      [FormKey.EndpointSuffix]: isEndpointSuffix,
      [FormKey.Name]: isName,
      [FormKey.Region]: isRegion,
      [FormKey.RootFolder]: isRootFolder,
      [FormKey.SecretKey]: isSecretKey,
      [FormKey.StorageType]: isStorageType
    } = context;
    const action = (condition: boolean) => (condition ? 'enable' : 'disable');

    formGroup.get(FormKey.AccessKey)[action(isAccessKey)]({ emitEvent: false });
    formGroup.get(FormKey.BucketName)[action(isBucketName)]({ emitEvent: false });
    formGroup.get(FormKey.CurrentSizeBytes)[action(isCurrentSizeBytes)]({ emitEvent: false });
    formGroup.get(FormKey.DeleteDate)[action(isDeleteDate)]({ emitEvent: false });
    formGroup.get(FormKey.Endpoint)[action(isEndpoint)]({ emitEvent: false });
    formGroup.get(FormKey.EndpointSuffix)[action(isEndpointSuffix)]({ emitEvent: false });
    formGroup.get(FormKey.Name)[action(isName)]({ emitEvent: false });
    formGroup.get(FormKey.Region)[action(isRegion)]({ emitEvent: false });
    formGroup.get(FormKey.RootFolder)[action(isRootFolder)]({ emitEvent: false });
    formGroup.get(FormKey.SecretKey)[action(isSecretKey)]({ emitEvent: false });
    formGroup.get(FormKey.StorageType)[action(isStorageType)]({ emitEvent: false });
  }
}
